
import React, { useState, useEffect, useRef } from "react";
import { useTheme } from "@repo/shared";
import { Container, Row, Col, Card, Button, Modal } from "react-bootstrap";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import BootstrapTable from 'react-bootstrap-table-next';
import UserDataService from "../../services/user/UserService";
import LoadingSpinner from '../common/LoadingSpinner';
import NoData from '../common/NoData';
import { ButtonDelete } from '../common/Buttons';
import SortCaret from '../common/SortCaret';

const roles = ['admin', 'superuser', 'user', 'artist', 'artist-viewer'];

const UserList = (props) => {
  const { appTheme } = useTheme();
  const [users, setUsers] = useState([]);
  const [userUnderEdit, setUserUnderEdit] = useState();
  const [loading, setLoading] = useState(true);
  const [deletingUserEmail, setDeletingUserEmail] = useState(null);
  const [resettingPassword, setResettingPassword] = useState(false);
  const editSnapshotRef = useRef(null);

  useEffect(() => {
    document.title = `${appTheme?.appName || ''} - Users`;
    retrieveUsers();
  }, []);

  const retrieveUsers = () => {
    UserDataService.getAll()
      .then((response) => {
        setUsers(response);
        setLoading(false);
      })
      .catch((e) => {
        console.log(e);
        setLoading(false);
      });
  };

  const checkSave = () => {
    if (!userUnderEdit) return true;

    const original = editSnapshotRef.current;
    const current = JSON.stringify(userUnderEdit);

    if (original === current) return true;

    return window.confirm('Do you want to discard changes?');
  };

  const closeUserEditor = () => {
    if (!checkSave()) return;

    setUserUnderEdit(null);
    editSnapshotRef.current = null;
  };

  const saveUser = async () => {
    setLoading(true);

    try {
      const updatedUsers = await UserDataService.create(userUnderEdit);
      setUserUnderEdit(null);
      editSnapshotRef.current = null;
      setUsers(updatedUsers);
    } catch (e) {
      console.error(e);
      window.alert('Unable to create the user. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const updateUser = async () => {
    setLoading(true);

    try {
      const updatedUsers = await UserDataService.update(
        userUnderEdit.id,
        userUnderEdit
      );
      setUserUnderEdit(null);
      editSnapshotRef.current = null;
      setUsers(updatedUsers);
    } catch (e) {
      console.error(e);
      window.alert('Unable to update the user. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetPassword = async () => {
    if (!userUnderEdit?.id || resettingPassword) return;

    const identifier = userUnderEdit.email || userUnderEdit.username;

    if (!window.confirm(
      `Are you sure you want to reset the password for ${identifier}?`
    )) {
      return;
    }

    setResettingPassword(true);

    try {
      await UserDataService.resetPassword(userUnderEdit.id);
      window.alert(`Password reset successfully for ${identifier}.`);
    } catch (e) {
      console.error('Unable to reset password:', e);
      window.alert('Unable to reset the password. Please try again.');
    } finally {
      setResettingPassword(false);
    }
  };

  const deleteUser = async (userToDelete) => {
    setDeletingUserEmail(userToDelete.email);

    try {
      const updatedUsers = await UserDataService.remove(userToDelete.id);
      setUsers(updatedUsers);
    } catch (e) {
      console.error(e);
      window.alert('Unable to delete the user. Please try again.');
    } finally {
      setDeletingUserEmail(null);
      setLoading(false);
    }
  };

  const startAddUser = () => {
    if (!checkSave()) return;

    const newUser = UserDataService.initialiseNewUser();
    editSnapshotRef.current = JSON.stringify(newUser);
    setUserUnderEdit(newUser);
  };

  const startEditUser = (userToEdit) => {
    if (!checkSave()) return;

    if (userToEdit) {
      const user = users.find(u => u.id === userToEdit.id);

      if (user) {
        editSnapshotRef.current = JSON.stringify(user);
        setUserUnderEdit({ ...user });
      }
    } else {
      setUserUnderEdit(null);
      editSnapshotRef.current = null;
    }
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setUserUnderEdit({ ...userUnderEdit, [name]: value });
  };

  const renderUserUnderEdit = () => {
    if (!userUnderEdit) return null;

    return (
      <Modal
        show={Boolean(userUnderEdit)}
        onHide={closeUserEditor}
        centered
        backdrop="static"
        keyboard={false}
        aria-labelledby="user-editor-title"
      >
        <form
          onSubmit={(event) => {
            event.preventDefault();

            if (userUnderEdit.id) {
              updateUser();
            } else {
              saveUser();
            }
          }}
        >
          <Modal.Header closeButton>
            <Modal.Title id="user-editor-title">
              {userUnderEdit.id ? 'Edit User' : 'Add User'}
            </Modal.Title>
          </Modal.Header>

          <Modal.Body>
            <div className="form-group mb-3">
              <label htmlFor="username" className="form-label">
                Username (*)
              </label>
              <input
                type="text"
                className="form-control"
                id="username"
                name="username"
                value={userUnderEdit.username || ''}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-group mb-3">
              <label htmlFor="displayName" className="form-label">
                Display Name (*)
              </label>
              <input
                type="text"
                className="form-control"
                id="displayName"
                name="displayName"
                value={userUnderEdit.displayName || ''}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="form-group mb-3">
              <label htmlFor="email" className="form-label">
                Email Address
              </label>
              <input
                type="email"
                className="form-control"
                id="email"
                name="email"
                value={userUnderEdit.email || ''}
                onChange={handleInputChange}
              />
            </div>

            <div className="form-group mb-3">
              <label htmlFor="role" className="form-label">
                Role
              </label>
              <select
                className="form-select"
                id="role"
                name="role"
                value={userUnderEdit.role || ''}
                onChange={handleInputChange}
              >
                <option value="" disabled>- Select Role -</option>
                {roles.map((role) => (
                  <option key={role} value={role}>
                    {role}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id="teacher"
                name="teacher"
                checked={Boolean(userUnderEdit.teacher)}
                onChange={(event) => {
                  setUserUnderEdit({
                    ...userUnderEdit,
                    teacher: event.target.checked
                  });
                }}
              />
              <label className="form-check-label" htmlFor="teacher">
                Teacher
              </label>
            </div>

            {userUnderEdit.id && (
              <>
                <hr className="my-4" />

                <div>
                  <div className="fw-semibold mb-1">Account Security</div>
                  <div className="text-muted small mb-3">
                    Reset this user's password independently of their profile
                    changes.
                  </div>

                  <Button
                    variant="outline-warning"
                    type="button"
                    onClick={resetPassword}
                    disabled={resettingPassword || loading}
                  >
                    {resettingPassword
                      ? 'Resetting Password…'
                      : 'Reset Password'}
                  </Button>
                </div>
              </>
            )}
          </Modal.Body>

          <Modal.Footer>
            <Button
              variant="outline-secondary"
              type="button"
              onClick={closeUserEditor}
              disabled={resettingPassword || loading}
            >
              Cancel
            </Button>

            <Button
              variant="primary"
              type="submit"
              disabled={loading || resettingPassword}
            >
              {loading
                ? 'Saving…'
                : (userUnderEdit.id ? 'Save Changes' : 'Create User')}
            </Button>
          </Modal.Footer>
        </form>
      </Modal>
    );
  };

  const userColumns = [
    {
      sort: true,
      dataField: 'username',
      text: 'Username',
      sortCaret: SortCaret
    },
    {
      sort: true,
      dataField: 'displayName',
      text: 'Display Name',
      sortCaret: SortCaret
    },
    {
      sort: true,
      dataField: 'email',
      text: 'Email',
      sortCaret: SortCaret
    },
    {
      sort: true,
      dataField: 'role',
      text: 'Role',
      sortCaret: SortCaret
    },
    {
      sort: true,
      dataField: 'teacher',
      text: 'Teacher',
      sortCaret: SortCaret,
      formatter: (cell) => cell ? 'Yes' : 'No'
    },
    {
      dataField: 'delete',
      text: '',
      editable: false,
      formatter: (cell, row) => (
        <ButtonDelete
          onClick={() => deleteUser(row)}
          isDeleting={deletingUserEmail === row.email}
          deleteConfirmationMessage={
            `Are you sure you want to delete the user with email ${row.email}?`
          }
          deleteConfirmationTitle="Delete User"
        />
      ),
      headerStyle: { width: '80px', textAlign: 'center' },
      align: 'center'
    }
  ];

  const tableNoDataIndication = () => {
    if (loading) {
      return <div className="p-4"><LoadingSpinner /></div>;
    }

    return <NoData type="students" />;
  };

  return (
    <Container className="py-2 py-lg-4">
      <Row className="justify-content-center">
        <Col>
          <Card className="h-100 shadow-sm">
            <Card.Header className="d-flex flex-column gap-2">
              <div className="d-flex align-items-center gap-2">
                <h2 className="mb-0">Users</h2>

                <Button
                  variant="primary"
                  size="sm"
                  onClick={startAddUser}
                  className="ms-auto"
                >
                  <FontAwesomeIcon icon={faPlus} className="me-2" />
                  Add User
                </Button>
              </div>
            </Card.Header>

            <Card.Body className="p-0" style={{ overflowX: 'auto' }}>
              <BootstrapTable
                keyField="id"
                striped
                hover
                condensed
                data={users}
                columns={userColumns}
                bordered={false}
                classes="mb-0"
                noDataIndication={tableNoDataIndication}
                rowEvents={{
                  onClick: (e, row) => startEditUser(row)
                }}
              />
            </Card.Body>

            <Card.Footer className="text-muted">
              Showing <strong>{users.length}</strong> users.
            </Card.Footer>
          </Card>
        </Col>
      </Row>

      {renderUserUnderEdit()}
    </Container>
  );
};

export default UserList;
  